// BottomArea component - bottom section with project/page breadcrumb
import type {
  BottomAreaConfig,
  BottomAreaAPI,
} from "../../../../../../types/stage-types.js";
import {
  createProjectsInContainer,
  type ProjectsAPI,
} from "./subcomponents/Projects/index.js";
import { createProjectsBrowser } from "./modals/ProjectsBrowser/index.js";
import {
  createPagesInContainer,
  type PagesAPI,
  type PageIconName,
} from "./subcomponents/Pages/index.js";
import {
  createShowAllInContainer,
  type ShowAllAPI,
} from "./subcomponents/ShowAll/index.js";
import { createIconButton, type IconButtonAPI } from "@UI/IconButton/index.js";
import { createPagesBrowser } from "./modals/PagesBrowser/index.js";
import { createSketchesBrowser } from "./modals/SketchesBrowser/index.js";
import { ModalAnchor } from "@/components/UI/Modal/index.js";
import type { BottomVisibilityConfig } from "@/components/Stage/subcomponents/UIControlsLayer/visibility-profiles";
import type { PageSummary, SketchSummary } from "@/types/project-types";

/**
 * Creates the BottomArea component with project/page breadcrumb
 * @param container - Container element for the BottomArea
 * @param config - Configuration with project and page names
 * @returns BottomArea API
 */
export const createBottomArea = (
  container: HTMLElement,
  config: BottomAreaConfig = {}
): BottomAreaAPI => {
  let isDestroyed = false;
  let currentProject = config.projectName ?? "Project";
  let currentPage = config.pageName ?? "Home";
  let currentPageIcon: PageIconName = "tools_component";
  let currentProjectId: string | null = null;
  let showAllAPI: ShowAllAPI | null = null;
  let showAllCompactAPI: ShowAllAPI | null = null;
  let projectsAPI: ProjectsAPI | null = null;
  let pagesAPI: PagesAPI | null = null;
  let compactExpandAPI: IconButtonAPI | null = null;
  let compactPrevAPI: IconButtonAPI | null = null;
  let compactNextAPI: IconButtonAPI | null = null;
  let compactAddAPI: IconButtonAPI | null = null;
  let expandedCollapseAPI: IconButtonAPI | null = null;
  // Selection is now centralized; BottomArea remains dumb
  let unsubscribeProjectChanged: (() => void) | null = null;
  let unsubscribePageChanged: (() => void) | null = null;
  let unsubscribeSketchChanged: (() => void) | null = null;
  let unsubscribeSketchCreated: (() => void) | null = null;
  let unsubscribeProjectsAvailable: (() => void) | null = null;
  let unsubscribeUIStateChanged: (() => void) | null = null;
  let unsubscribeToolsLevelChanged: (() => void) | null = null;
  const appStateManager = config.appStateManager;
  const uiState = appStateManager?.uiState;
  const eventBus = appStateManager?.appContext?.eventBus;

  let bottomRootEl: HTMLElement | null = null;
  let floatingPanelEl: HTMLElement | null = null;
  let compactPanelEl: HTMLElement | null = null;
  let projectContainerEl: HTMLElement | null = null;
  let showAllContainerEl: HTMLElement | null = null;
  let showAllCompactContainerEl: HTMLElement | null = null;
  let pagesContainerEl: HTMLElement | null = null;
  let compactLabelEl: HTMLElement | null = null;
  let compactCountEl: HTMLElement | null = null;
  let currentPages: PageSummary[] = [];
  let currentSketches: SketchSummary[] = [];
  let currentPageId: string | null = null;
  let currentSketchId: string | null = null;
  let currentSketchLabel = "Untitled Sketch";
  let toolsLevel: "main" | "sub" = "main";
  let forceExpanded = false;

  const showAllModes = new Set<"edit" | "sketch">(["edit", "sketch"]);
  const initialState = uiState?.getState?.() ?? null;
  let currentMode: string =
    (initialState?.selectedMode as string) || "edit";
  let showAllByMode: Record<"edit" | "sketch", boolean> = {
    edit: !!initialState?.show_all?.edit,
    sketch: !!initialState?.show_all?.sketch,
  };
  let currentShowAllLabel = config.initialShowAllLabel ?? "";

  const getShowAllForMode = (mode: string): boolean => {
    if (mode === "edit") return showAllByMode.edit;
    if (mode === "sketch") return showAllByMode.sketch;
    return false;
  };

  const syncShowAllFromState = (state: any): void => {
    if (!state) return;
    const nextMode =
      typeof state.selectedMode === "string" ? state.selectedMode : currentMode;
    if (nextMode) currentMode = nextMode;

    const stateShowAll = state.show_all;
    if (stateShowAll && typeof stateShowAll === "object") {
      if (typeof stateShowAll.edit === "boolean") {
        showAllByMode = { ...showAllByMode, edit: stateShowAll.edit };
      }
      if (typeof stateShowAll.sketch === "boolean") {
        showAllByMode = { ...showAllByMode, sketch: stateShowAll.sketch };
      }
    }

    const isAllowedMode = showAllModes.has(
      currentMode as "edit" | "sketch"
    );
    const nextSelected = isAllowedMode ? getShowAllForMode(currentMode) : false;
    showAllAPI?.setSelected(nextSelected);
    showAllCompactAPI?.setSelected(nextSelected);
    if (currentMode === "sketch") {
      pagesAPI?.setLabel(currentSketchLabel);
      pagesAPI?.setIcon("modes_sketch");
    } else if (currentMode === "edit") {
      pagesAPI?.setLabel(currentPage);
      pagesAPI?.setIcon(currentPageIcon);
    }
    updateCompactNavState();
  };

  const handleShowAllToggle = (selected: boolean): void => {
    if (isDestroyed) return;
    if (!uiState) return;

    if (!showAllModes.has(currentMode as "edit" | "sketch")) {
      showAllAPI?.setSelected(false);
      return;
    }

    const modeKey = currentMode as "edit" | "sketch";
    if (showAllByMode[modeKey] === selected) {
      return;
    }

    showAllByMode = { ...showAllByMode, [modeKey]: selected };

    uiState.setShowAll(modeKey, selected);
    eventBus?.emit("show-all-changed", {
      mode: modeKey,
      value: selected,
      label: currentShowAllLabel,
      source: "local",
    });
  };

  const setCompactState = (compact: boolean): void => {
    const root = bottomRootEl ?? container;
    root.classList.toggle("bottom--compact", compact);
    root.classList.toggle("bottom--expanded", !compact);
  };

  const applyCompactState = (): void => {
    const shouldCompact = toolsLevel === "sub" && !forceExpanded;
    setCompactState(shouldCompact);
    compactExpandAPI?.setSelected(forceExpanded);
  };

  const resolveNavItems = (): {
    items: { id: string; label: string }[];
    selectedId: string | null;
    label: string;
  } => {
    if (currentMode === "sketch") {
      return {
        items: currentSketches.map((sketch) => ({
          id: sketch.id,
          label: sketch.title ?? "Untitled Sketch",
        })),
        selectedId: currentSketchId,
        label: currentSketchLabel,
      };
    }

    return {
      items: currentPages.map((page) => ({
        id: page.id,
        label: page.name,
      })),
      selectedId: currentPageId,
      label: currentPage,
    };
  };

  const updateCompactNavState = (): void => {
    const { items, selectedId, label } = resolveNavItems();
    const total = items.length;
    const currentIndex = selectedId
      ? items.findIndex((item) => item.id === selectedId)
      : -1;

    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex >= 0 && currentIndex < total - 1;
    compactPrevAPI?.setDisabled(!hasPrev);
    compactNextAPI?.setDisabled(!hasNext);

    if (compactCountEl) {
      compactCountEl.textContent =
        currentIndex >= 0 && total > 0
          ? `${currentIndex + 1}/${total}`
          : total > 0
            ? `0/${total}`
            : "";
      compactCountEl.style.display = compactCountEl.textContent ? "" : "none";
    }

    if (compactLabelEl) {
      compactLabelEl.textContent = label ?? "";
      compactLabelEl.style.display = compactLabelEl.textContent ? "" : "none";
    }

    const addAllowed = currentMode === "sketch" && !!currentProjectId;
    compactAddAPI?.setDisabled(!addAllowed);
  };

  const emitNavigateToNext = (direction: "prev" | "next"): void => {
    const { items, selectedId } = resolveNavItems();
    if (!items.length || !selectedId) return;
    const currentIndex = items.findIndex((item) => item.id === selectedId);
    if (currentIndex < 0) return;
    const targetIndex =
      direction === "prev" ? currentIndex - 1 : currentIndex + 1;
    const target = items[targetIndex];
    if (!target) return;
    if (currentMode === "sketch") {
      eventBus?.emit("sketch-selected", { sketchId: target.id });
    } else {
      eventBus?.emit("page-selected", { pageId: target.id });
    }
  };

  const requestSketchCreate = (): void => {
    if (!currentProjectId) return;
    eventBus?.emit("sketch-create-requested", {
      projectId: currentProjectId,
    });
  };

  /**
   * Initialize BottomArea HTML structure
   */
  const initBottomArea = (): void => {
    container.innerHTML = `
      <div class="bottom">
        <div class="project-page-gradient">
          <!-- Floating panel: hor-flex with gap:10px, padding left-right: 20px -->
          <div class="floating-panel floating-panel--expanded container--active">
            <!-- project: 236px width, 44px height -->
            <div class="project"></div>
            <!-- show all: flex-hor, 88px width, 44px height -->
            <div class="show-all-container"></div>
            <!-- page selector: 140px width, 44px height -->
            <div class="page-selector"></div>
            <div class="expand-toggle"></div>
          </div>
          <div class="floating-panel floating-panel--compact">
            <div class="compact-expand"></div>
            <div class="compact-prev"></div>
            <div class="compact-show-all"></div>
            <div class="compact-add"></div>
            <div class="compact-next"></div>
            <div class="compact-meta">
              <span class="compact-label"></span>
              <span class="compact-count"></span>
            </div>
          </div>
        </div>
      </div>
    `;

    bottomRootEl = container.querySelector(".bottom") as HTMLElement;
    floatingPanelEl = container.querySelector(
      ".floating-panel"
    ) as HTMLElement;
    compactPanelEl = container.querySelector(
      ".floating-panel--compact"
    ) as HTMLElement;
    compactLabelEl = container.querySelector(
      ".compact-label"
    ) as HTMLElement;
    compactCountEl = container.querySelector(
      ".compact-count"
    ) as HTMLElement;

    const ModalLocDef: {
      offset: { x: number; y: number };
      anchor: ModalAnchor;
      width: number | string;
    } = {
      offset: { x: 0, y: 100 },
      anchor: "bottom-center",
      width: 480,
    };

    // Initialize Actions component
    const showAllContainer = container.querySelector(
      ".show-all-container"
    ) as HTMLElement;
    if (showAllContainer) {
      try {
        showAllAPI = createShowAllInContainer(showAllContainer, {
          label: config.initialShowAllLabel ?? "",
          initialSelected: false,
          onToggle: (selected) => {
            handleShowAllToggle(selected);
          },
        });
        showAllContainerEl = showAllContainer;
      } catch (error) {
        console.error("Failed to create ShowAll component:", error);
      }
    }

    const compactShowAllContainer = container.querySelector(
      ".compact-show-all"
    ) as HTMLElement;
    if (compactShowAllContainer) {
      try {
        showAllCompactAPI = createShowAllInContainer(
          compactShowAllContainer,
          {
            label: config.initialShowAllLabel ?? "",
            initialSelected: false,
            onToggle: (selected) => {
              handleShowAllToggle(selected);
            },
          }
        );
        showAllCompactContainerEl = compactShowAllContainer;
      } catch (error) {
        console.error("Failed to create compact ShowAll component:", error);
      }
    }

    if (initialState) {
      syncShowAllFromState(initialState);
    }

    // Initialize Projects component inside .project
    const projectContainer = container.querySelector(".project") as HTMLElement;
    if (projectContainer) {
      try {
        projectsAPI = createProjectsInContainer(projectContainer, {
          projectName: currentProject,
          versionLabel: "v1",
          totalVersions: 2,
          icon: "project_web",
          onChevronClick: async () => {
            // Open browser from last known list (subscribed via projects-available)
            const projects: any[] =
              (createBottomArea as any).__projectsList || [];
            const selectedProjectId: string | null =
              (createBottomArea as any).__selectedProjectId || null;
            const browser = createProjectsBrowser({
              projects,
              selectedProjectId,
              width: ModalLocDef.width,
              anchor: ModalLocDef.anchor,
              offset: ModalLocDef.offset,
              onSelect: (projectId: string) => {
                // Emit selection event; controller will handle persistence and state
                const eb = (config as any)?.appStateManager?.appContext
                  ?.eventBus;
                eb?.emit("project-selected", { projectId });
              },
            });
            browser.show();
          },
        });
        projectContainerEl = projectContainer;
      } catch (error) {
        console.error("Failed to create Projects component:", error);
      }
    }

    // Initialize Pages component inside .page-selector
    const pageSelectorContainer = container.querySelector(
      ".page-selector"
    ) as HTMLElement;
    if (pageSelectorContainer) {
      try {
        pagesAPI = createPagesInContainer(pageSelectorContainer, {
          label: currentPage,
          icon: "tools_component",
          onChevronClick: async (_anchorEl) => {
            if (currentMode === "sketch") {
              const browser = createSketchesBrowser({
                sketches: currentSketches,
                selectedSketchId: currentSketchId,
                width: ModalLocDef.width,
                anchor: ModalLocDef.anchor,
                offset: ModalLocDef.offset,
                onSelect: (sketchId: string) => {
                  const eb = (config as any)?.appStateManager?.appContext
                    ?.eventBus;
                  eb?.emit("sketch-selected", { sketchId });
                },
                onAddNew: () => {
                  requestSketchCreate();
                },
              });
              browser.show();
              return;
            }

            const browser = createPagesBrowser({
              pages: currentPages,
              selectedPageId: currentPageId,
              width: ModalLocDef.width,
              anchor: ModalLocDef.anchor,
              offset: ModalLocDef.offset,
              onSelect: (pageId: string) => {
                const eb = (config as any)?.appStateManager?.appContext
                  ?.eventBus;
                eb?.emit("page-selected", { pageId });
              },
            });
            browser.show();
          },
        });
        pagesContainerEl = pageSelectorContainer;
      } catch (error) {
        console.error("Failed to create Pages component:", error);
      }
    }

    const compactExpandContainer = container.querySelector(
      ".compact-expand"
    ) as HTMLElement;
    if (compactExpandContainer) {
      try {
        compactExpandAPI = createIconButton({
          icon: "alignment_top",
          behavior: "switch",
          size: 32,
          iconSize: 24,
          rounded: true,
          transparentBg: true,
          selected: false,
          onClick: (selected) => {
            forceExpanded = !!selected;
            applyCompactState();
          },
        });
        compactExpandContainer.appendChild(compactExpandAPI.getElement());
      } catch (error) {
        console.error("Failed to create compact expand button:", error);
      }
    }

    const expandToggleContainer = container.querySelector(
      ".expand-toggle"
    ) as HTMLElement;
    if (expandToggleContainer) {
      try {
        expandedCollapseAPI = createIconButton({
          icon: "alignment_bottom",
          size: 28,
          iconSize: 24,
          rounded: true,
          transparentBg: true,
          onClick: () => {
            forceExpanded = false;
            applyCompactState();
          },
        });
        expandToggleContainer.appendChild(expandedCollapseAPI.getElement());
      } catch (error) {
        console.error("Failed to create expanded collapse button:", error);
      }
    }

    const compactPrevContainer = container.querySelector(
      ".compact-prev"
    ) as HTMLElement;
    if (compactPrevContainer) {
      try {
        compactPrevAPI = createIconButton({
          icon: "modifiers_undo",
          size: 32,
          iconSize: 24,
          rounded: true,
          transparentBg: true,
          onClick: () => {
            emitNavigateToNext("prev");
          },
        });
        compactPrevContainer.appendChild(compactPrevAPI.getElement());
      } catch (error) {
        console.error("Failed to create compact prev button:", error);
      }
    }

    const compactAddContainer = container.querySelector(
      ".compact-add"
    ) as HTMLElement;
    if (compactAddContainer) {
      try {
        compactAddAPI = createIconButton({
          icon: "erasers_new_canvas",
          size: 32,
          iconSize: 24,
          rounded: true,
          transparentBg: true,
          onClick: () => {
            requestSketchCreate();
          },
        });
        compactAddContainer.appendChild(compactAddAPI.getElement());
      } catch (error) {
        console.error("Failed to create compact add button:", error);
      }
    }

    const compactNextContainer = container.querySelector(
      ".compact-next"
    ) as HTMLElement;
    if (compactNextContainer) {
      try {
        compactNextAPI = createIconButton({
          icon: "modifiers_redo",
          size: 32,
          iconSize: 24,
          rounded: true,
          transparentBg: true,
          onClick: () => {
            emitNavigateToNext("next");
          },
        });
        compactNextContainer.appendChild(compactNextAPI.getElement());
      } catch (error) {
        console.error("Failed to create compact next button:", error);
      }
    }

    // Subscribe to centralized selection events
    const eventBus = (config as any)?.appStateManager?.appContext?.eventBus;
    if (eventBus) {
      unsubscribeProjectsAvailable = eventBus.on(
        "projects-available",
        (data: any) => {
          (createBottomArea as any).__projectsList = data.projects;
          (createBottomArea as any).__selectedProjectId =
            data.selectedProjectId;
        }
      );

      unsubscribeProjectChanged = eventBus.on(
        "project-changed",
        (data: any) => {
          currentProjectId = data.projectId ?? null;
          currentProject = data.projectName;
          currentPages = Array.isArray(data.pages) ? [...data.pages] : [];
          currentPageId = data.selectedPageId ?? null;
          currentSketches = Array.isArray(data.sketches)
            ? [...data.sketches]
            : [];
          currentSketchId = data.selectedSketchId ?? null;
          projectsAPI?.setProjectName(currentProject);
          // Update project icon based on type
          const type = data.projectType as "website" | "dashboard" | "app";
          const map: Record<string, any> = {
            website: "project_web",
            dashboard: "project_dashboard",
            app: "project_app",
          };
          projectsAPI?.setIcon(map[type] || "project_web");
          const selectedPage =
            (data.selectedPage && data.selectedPage.name) ||
            (Array.isArray(data.pages)
              ? data.pages.find(
                  (page: any) => page.id === data.selectedPageId
                )?.name
              : null);
          if (selectedPage) {
            pagesAPI?.setLabel(selectedPage);
            currentPage = selectedPage;
          }
          const selectedPageType =
            data.selectedPage?.type ??
            (Array.isArray(data.pages)
              ? data.pages.find(
                  (page: any) => page.id === data.selectedPageId
                )?.type
              : null);
          if (selectedPageType) {
            const iconMap: Record<string, PageIconName> = {
              home: "page_home",
              slug: "page_slug",
              component: "tools_component",
            };
            currentPageIcon = iconMap[selectedPageType] ?? "tools_component";
            pagesAPI?.setIcon(currentPageIcon);
          }
          if (data.selectedSketch) {
            currentSketchLabel =
              data.selectedSketch.title ?? "Untitled Sketch";
            currentSketchId = data.selectedSketch.id ?? currentSketchId;
          } else if (Array.isArray(data.sketches)) {
            const selectedSketch =
              data.sketches.find(
                (item: any) => item.id === data.selectedSketchId
              ) ?? data.sketches[0];
            if (selectedSketch) {
              currentSketchLabel =
                selectedSketch.title ?? "Untitled Sketch";
              currentSketchId = selectedSketch.id ?? currentSketchId;
            }
          }
          updateBreadcrumb();
          updateCompactNavState();
        }
      );

      unsubscribePageChanged = eventBus.on("page-changed", (data: any) => {
        currentPage = data.pageName;
        currentPageId = data.pageId ?? null;
        pagesAPI?.setLabel(currentPage);
        // Update page icon based on page type
        const pageType = data.pageType as
          | "home"
          | "slug"
          | "component"
          | undefined;
        const iconMap: Record<string, any> = {
          home: "page_home",
          slug: "page_slug",
          component: "tools_component",
        };
        currentPageIcon = iconMap[pageType || "component"];
        pagesAPI?.setIcon(currentPageIcon);
        updateBreadcrumb();
        updateCompactNavState();
      });

      unsubscribeSketchChanged = eventBus.on(
        "sketch-changed",
        (data: any) => {
          if (!data) return;
          if (typeof data.projectId === "string") {
            currentProjectId = data.projectId;
          }
          currentSketchId = data.sketchId ?? currentSketchId;
          currentSketchLabel = data.sketchName ?? currentSketchLabel;
          if (currentMode === "sketch") {
            pagesAPI?.setLabel(currentSketchLabel);
            pagesAPI?.setIcon("modes_sketch");
          }
          updateCompactNavState();
        }
      );

      unsubscribeUIStateChanged = eventBus.on(
        "ui-state-changed",
        (data: any) => {
          syncShowAllFromState(data?.state);
        }
      );

      unsubscribeToolsLevelChanged = eventBus.on(
        "tools-level-changed",
        (payload: any) => {
          toolsLevel = payload?.level === "sub" ? "sub" : "main";
          applyCompactState();
        }
      );

      unsubscribeSketchCreated = eventBus.on(
        "sketch-created",
        (payload: any) => {
          const sketch = payload?.sketch as
            | (SketchSummary & { projectId?: string; lastModifiedBy?: string })
            | undefined;
          if (!sketch) return;
          const summary: SketchSummary = {
            id: sketch.id,
            projectId:
              (sketch as any).projectId ?? currentProjectId ?? "",
            title: sketch.title ?? "Untitled Sketch",
            updatedAt:
              typeof (sketch as any).updatedAt === "string"
                ? (sketch as any).updatedAt
                : new Date().toISOString(),
            version:
              typeof (sketch as any).version === "number"
                ? (sketch as any).version
                : 0,
            lastModifiedBy:
              typeof (sketch as any).lastModifiedBy === "string"
                ? (sketch as any).lastModifiedBy
                : "system",
            previews: Array.isArray((sketch as any).previews)
              ? (sketch as any).previews
              : undefined,
          };
          currentSketches = [...currentSketches, summary];
          currentSketchId = summary.id;
          currentSketchLabel = summary.title ?? "Untitled Sketch";
          if (currentMode === "sketch") {
            pagesAPI?.setLabel(currentSketchLabel);
            pagesAPI?.setIcon("modes_sketch");
          }
          updateCompactNavState();
        }
      );

      // Request current data immediately after subscriptions
      eventBus.emit("request-projects", {});
      eventBus.emit("request-current-selection", {});
      eventBus.emit("request-tools-level", {});
    }
  };

  /**
   * Update project name
   * @param project - New project name
   */
  const updateProject = (project: string): void => {
    if (isDestroyed) return;

    currentProject = project;
    updateBreadcrumb();
    projectsAPI?.setProjectName(project);
  };

  /**
   * Update page name
   * @param page - New page name
   */
  const updatePage = (page: string): void => {
    if (isDestroyed) return;

    currentPage = page;
    updateBreadcrumb();
    pagesAPI?.setLabel(page);
    updateCompactNavState();
  };

  const applyVisibility = (visibility: BottomVisibilityConfig): void => {
    if (isDestroyed) return;

    const areaVisible = visibility.visible !== false;
    container.style.display = areaVisible ? "" : "none";
    if (floatingPanelEl) {
      floatingPanelEl.style.display = areaVisible ? "" : "none";
    }
    if (compactPanelEl) {
      compactPanelEl.style.display = areaVisible ? "" : "none";
    }

    if (projectContainerEl) {
      projectContainerEl.style.display = visibility.projects.visible ? "" : "none";
    }

    if (showAllContainerEl) {
      showAllContainerEl.style.display = visibility.showAll.visible ? "" : "none";
      if (visibility.showAll.label) {
        showAllContainerEl.setAttribute("title", visibility.showAll.label);
      } else {
        showAllContainerEl.removeAttribute("title");
      }
      const label = visibility.showAll.label ?? currentShowAllLabel;
      currentShowAllLabel = label;
      showAllAPI?.setLabel(label);
      showAllAPI?.setVisible(visibility.showAll.visible);
      showAllCompactAPI?.setLabel(label);
      showAllCompactAPI?.setVisible(visibility.showAll.visible);
    }

    if (pagesContainerEl) {
      pagesContainerEl.style.display = visibility.pages.visible ? "" : "none";
      if (visibility.pages.label) {
        pagesContainerEl.setAttribute(
          "data-visibility-label",
          visibility.pages.label
        );
      } else {
        pagesContainerEl.removeAttribute("data-visibility-label");
      }
    }

    if (showAllCompactContainerEl) {
      showAllCompactContainerEl.style.display =
        visibility.showAll.visible ? "" : "none";
    }

    if (currentMode === "sketch") {
      pagesAPI?.setIcon("modes_sketch");
    }
  };

  /**
   * Update breadcrumb display
   */
  const updateBreadcrumb = (): void => {
    const breadcrumbText = currentPage
      ? `/ ${currentProject} / ${currentPage}`
      : `/ ${currentProject}`;
    const breadcrumbElement = container.querySelector(
      "#breadcrumb-text"
    ) as HTMLElement;
    if (breadcrumbElement) {
      breadcrumbElement.textContent = breadcrumbText;
    }
  };

  /**
   * Set component visibility
   * @param visible - Visibility state
   */
  const setVisible = (visible: boolean): void => {
    if (isDestroyed) return;

    container.style.visibility = visible ? "visible" : "hidden";
  };

  /**
   * Cleanup resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    showAllAPI?.destroy();
    showAllAPI = null;
    showAllCompactAPI?.destroy();
    showAllCompactAPI = null;

    compactExpandAPI?.destroy();
    compactExpandAPI = null;
    compactPrevAPI?.destroy();
    compactPrevAPI = null;
    compactNextAPI?.destroy();
    compactNextAPI = null;
    compactAddAPI?.destroy();
    compactAddAPI = null;
    expandedCollapseAPI?.destroy();
    expandedCollapseAPI = null;

    // Clean up Projects component
    projectsAPI?.destroy();
    projectsAPI = null;

    // Clean up Pages component
    pagesAPI?.destroy();
    pagesAPI = null;

    container.innerHTML = "";

    // Unsubscribe
    unsubscribeProjectChanged?.();
    unsubscribePageChanged?.();
    unsubscribeProjectsAvailable?.();
    unsubscribeUIStateChanged?.();
    unsubscribeSketchChanged?.();
    unsubscribeToolsLevelChanged?.();
    unsubscribeSketchCreated?.();
    unsubscribeProjectChanged = null;
    unsubscribePageChanged = null;
    unsubscribeProjectsAvailable = null;
    unsubscribeUIStateChanged = null;
    unsubscribeSketchChanged = null;
    unsubscribeToolsLevelChanged = null;
    unsubscribeSketchCreated = null;
  };

  // Initialize component
  initBottomArea();
  // No local restore here; controller will emit initial state
  applyCompactState();
  updateCompactNavState();

  // Return API
  return {
    updateProject,
    updatePage,
    applyVisibility,
    setVisible,
    destroy,
  };
};
